// Accordion variant of the post creator. As of May 26 this is the active
// wizard (/app is legacy); category selection happens upstream on /create.
//
// Section sequence is route-branched based on the ?type= URL param:
//   product-shot / carousel / single (default):
//     Platform → Upload → Details → Background → Quality → Preview → Add Text & Logo
//   fashion:
//     Upload → Details → Fashion Model → Quality → Preview → Add Text & Logo
//   single (Social Post):
//     Platform → Upload → Details → Social Design → Social Final
//
// PostType arrives via ?type= and is FIXED for the session — there is no
// in-wizard category switcher anymore. If the user wants a different
// category, they go back to /create and start over (which wipes state
// cleanly). If ?type= is missing or invalid, this page redirects to /create.
//
// Each section renders the same StepX component as /app does in compact
// mode (StepX's Section wrapper supplies the icon + title + description,
// inner StepHeader is suppressed). Sections that auto-advance in /app
// use explicit Continue buttons here so the user reviews their pick.

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import Section from './components/Section';
import DesignStep, { type DesignMode } from './components/DesignStep';
import AiGeneratePanel, { CreativeDirectionFilters } from './components/AiGeneratePanel';
import AiResultViewer from './components/AiResultViewer';
import type { PostType } from '../app/components/StepCategory';
import StepPlatform from '../app/components/StepPlatform';
import StepUpload from '../app/components/StepUpload';
import StepConfirmCutout from '../app/components/StepConfirmCutout';
import StepDetails from '../app/components/StepDetails';
import StepBackground from '../app/components/StepBackground';
import StepFashionModel from '../app/components/StepFashionModel';
import StepProductSwapScene from '../app/components/StepProductSwapScene';
import StepSocialDesign, { type SocialDesignId } from '../app/components/StepSocialDesign';
import StepSocialFinal from '../app/components/StepSocialFinal';
import StepQuality from '../app/components/StepQuality';
import StepPreview from '../app/components/StepPreview';
import StepText from '../app/components/StepText';
import type { LogoOverlay, ShapeOverlay, SuggestedFrame, TextOverlay } from '../app/lib/composeRender';
import { socialPlatforms, productShotFormats, socialPostFormats, lockedSquareFormats, type ProductSilhouette } from '@/mocks/app-data';
import {
  type CreativeDirection,
  type Quality,
  type Provider,
  type SocialPostSlots,
  type UserProduct,
  type WizardAiGenerateResult,
  suggestCopy,
  listProducts,
  getBrand,
  wizardAiGenerate,
  InsufficientCreditsError,
} from '@/lib/api';
import { useCredits } from '@/hooks/useCredits';
import { useBrand } from '@/hooks/useBrand';

type SectionId =
  | 'platform'
  | 'upload'
  | 'confirm-cutout'
  | 'details'
  | 'background'
  | 'fashion-model'
  | 'product-swap-scene'
  | 'social-design'
  | 'social-final'
  | 'quality'
  | 'preview'
  | 'text';

// Default routes (product-shot / carousel / single fall here) — 7 sections.
// Category section was removed May 26 (now selected upstream on /create).
const DEFAULT_SECTIONS: readonly SectionId[] = [
  'platform', 'upload', 'details', 'background', 'quality', 'preview', 'text',
];

// Carousel — 6 sections. No Platform step (all 3 carousel platforms are
// 1080×1080 so the picker would be a no-op). Platform auto-set to
// CAROUSEL_DEFAULT_PLATFORM on mount when ?type=carousel.
const CAROUSEL_SECTIONS: readonly SectionId[] = [
  'upload', 'details', 'background', 'quality', 'preview', 'text',
];

const CAROUSEL_DEFAULT_PLATFORM = 'instagram-carousel';

// Fashion — 7 sections. Platform step shows lockedSquareFormats (1:1
// active, 4:5 + 9:16 coming-soon, no 16:9). Output is locked 1:1 in
// v1 because mask-based fashion templates are hand-authored at
// 1024×1024; the picker exists to surface the roadmap, not because
// the user can change anything. Fashion Model picker slots in after
// Details.
const FASHION_SECTIONS: readonly SectionId[] = [
  'platform', 'upload', 'details', 'fashion-model', 'quality', 'preview', 'text',
];

// Product Swap — 7 sections, same shape as Fashion. Platform shows
// lockedSquareFormats. Output is 1:1 because mask-based scene templates
// are hand-authored at 1024×1024. Plain product upload — no garment
// gating.
const PRODUCT_SWAP_SECTIONS: readonly SectionId[] = [
  'platform', 'upload', 'details', 'product-swap-scene', 'quality', 'preview', 'text',
];

// Social Post — 6 sections. No OpenAI image generation: bg-removal runs
// on the uploaded product (Confirm cutout step), then SocialFinal
// composites client-side. AI is used only for text content.
const SOCIAL_SECTIONS: readonly SectionId[] = [
  'platform', 'upload', 'confirm-cutout', 'details', 'social-design', 'social-final',
];

// Video route was here (6 sections, video-generation final step) — removed
// May 26 with the rest of the video code. See VIDEO-RESEARCH.md.

// Short titles + icons used by the sidebar nav. Kept separate from the
// in-section header copy (which uses longer titles + descriptions). Same
// icon set as the in-section header so the visual anchor is consistent
// between sidebar and content.
const STEP_META: Record<SectionId, { title: string; icon: string }> = {
  'platform':         { title: 'Platform',   icon: 'ri-smartphone-line' },
  'upload':           { title: 'Upload',     icon: 'ri-upload-cloud-line' },
  'confirm-cutout':   { title: 'Crop',       icon: 'ri-crop-line' },
  'details':          { title: 'Details',    icon: 'ri-sparkling-2-line' },
  'background':       { title: 'Background', icon: 'ri-palette-line' },
  'fashion-model':    { title: 'Model',      icon: 'ri-user-line' },
  'product-swap-scene': { title: 'Scene',    icon: 'ri-hand-heart-line' },
  'social-design':    { title: 'Design',     icon: 'ri-layout-grid-line' },
  'social-final':     { title: 'Edit',       icon: 'ri-magic-line' },
  'quality':          { title: 'Quality',    icon: 'ri-vip-crown-line' },
  'preview':          { title: 'Preview',    icon: 'ri-image-line' },
  'text':             { title: 'Add Text & Logo', icon: 'ri-text' },
};

type Garment = 'top' | 'bottoms' | 'shoes' | 'none';
type GarmentRestriction = 'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none';

export default function AppV2Page() {
  const navigate = useNavigate();
  const { balance, planTier, refresh: refreshCredits } = useCredits();
  const { activeBrand } = useBrand();

  // ---------- Wizard state (mirror of /app/page.tsx) ----------
  // PostType arrives via ?type= URL param (set by the /create dashboard).
  // Parsed eagerly so the section-array selection below is correct on
  // first render. If the param is missing or invalid, we redirect via a
  // mount effect (can't redirect during render — would suspend).
  const [searchParams] = useSearchParams();
  const VALID_TYPES: readonly PostType[] = ['product-shot', 'fashion', 'product-swap', 'carousel', 'single'];
  const urlType = searchParams.get('type');
  const initialPostType = (
    urlType && (VALID_TYPES as readonly string[]).includes(urlType)
      ? (urlType as PostType)
      : null
  );

  const [step, setStep] = useState(1);
  const [postType] = useState<PostType | null>(initialPostType);
  // Carousel pre-picks its single 1080×1080 default. Fashion + Product
  // Swap pre-pick locked-1x1 since it's the only `available` option
  // (4:5 + 9:16 are coming-soon — surfaced as disabled tiles for the
  // roadmap, can't be selected). Other routes start with no platform.
  const [platform, setPlatform] = useState<string | null>(
    initialPostType === 'carousel'
      ? CAROUSEL_DEFAULT_PLATFORM
      : initialPostType === 'fashion' || initialPostType === 'product-swap'
        ? 'locked-1x1'
        : null,
  );
  const [productImages, setProductImages] = useState<string[]>([]);
  const [productFiles, setProductFiles] = useState<File[]>([]);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [, setCostSpent] = useState(0);
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>([]);
  const [shapeOverlays, setShapeOverlays] = useState<ShapeOverlay[]>([]);
  // Brand-logo overlays on the final step. One per frame max in v1.
  // Cleared on every new upload (handleUpload) so a fresh product
  // starts with a clean slate.
  const [logoOverlays, setLogoOverlays] = useState<LogoOverlay[]>([]);
  // User's saved brand logo URL (from /api/brand). Null when not set
  // yet — StepText shows a "Set up in My Brand" prompt in that case.
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const [sceneId, setSceneId] = useState<string | null>(null);
  const [fashionModelId, setFashionModelId] = useState<string | null>(null);
  // Product Swap route — selected scene template + the product's detected
  // silhouette (from analyze-product) that drives scene recommendation.
  const [productSwapSceneId, setProductSwapSceneId] = useState<string | null>(null);
  const [productSilhouette, setProductSilhouette] = useState<ProductSilhouette | null>(null);
  // Concept handed off from the Ad Strategist ("Generate this →"). Read once
  // from sessionStorage on mount and shown as a reference banner so the user
  // has the headline/visual direction while they build.
  const [adConcept, setAdConcept] = useState<{ headline?: string; subhead?: string; visual_direction?: string; template_hint?: string } | null>(null);
  const [socialDesignId, setSocialDesignId] = useState<SocialDesignId | null>(null);
  const [socialSlots, setSocialSlots] = useState<SocialPostSlots | null>(null);

  // Confirmation modals — show before destructive nav (New Creative wipes
  // state; Gallery leaves the page entirely). Only fired when the user
  // has unsaved progress; otherwise the corresponding action runs
  // immediately and the modal is skipped.
  const [showNewPostConfirm, setShowNewPostConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [quality, setQuality] = useState<Quality>('low');
  const [provider, setProvider] = useState<Provider>('openai');
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [suggestedFrames, setSuggestedFrames] = useState<SuggestedFrame[]>([]);
  const [lastGenKey, setLastGenKey] = useState<string | null>(null);

  // Fashion-route auto-detected fields from /api/analyze-product.
  const [garmentType, setGarmentType] = useState<Garment | null>(null);
  const [garmentRestriction, setGarmentRestriction] = useState<GarmentRestriction | null>(null);
  // Product-shot soft warning when an uploaded photo has a human/model
  // — surfaces a nudge to switch to Fashion route. Non-blocking.
  const [hasHumanModel, setHasHumanModel] = useState<boolean | null>(null);
  // Drives StepBackground's "Recommended for you" tab. Populated from
  // /api/analyze-product (vision) OR /api/scrape-product (text-only LLM).
  const [productCategory, setProductCategory] = useState<string | null>(null);
  const [productTags, setProductTags] = useState<string[]>([]);

  // Saved-products library (My Products). Loaded once on mount; the
  // Upload section uses it to render its LEFT picker pane. Empty array
  // = no library yet; null = still loading; on error we silently fall
  // back to the empty-state UI (the URL + manual-upload paths still work).
  const [savedProducts, setSavedProducts] = useState<UserProduct[] | null>(null);
  // Id of the saved product currently driving the upload (when the user
  // picked from the library). Drives the selected-tile visual state in
  // StepUpload. Cleared whenever handleUpload runs (so URL-scrape /
  // file-picker uploads deselect the previously-picked tile).
  const [selectedSavedProductId, setSelectedSavedProductId] = useState<string | null>(null);
  // Multi-product carousel — ordered array of all library-picked ids.
  // Mirrors the order of productImages / productFiles so the StepUpload
  // tiles can show the right position badge per pick. Cleared on a
  // fresh upload (same wipe rules as the rest of product state).
  const [selectedSavedProductIds, setSelectedSavedProductIds] = useState<string[]>([]);
  // Per-product name + description for the multi-product carousel
  // path. Mirrors productFiles index-for-index. Library/URL picks
  // pre-populate these with cached metadata; manual file uploads
  // append empty strings that StepDetails will fill via analyze.
  // The legacy `productName` / `productDescription` (singular) still
  // exist for single-product routes; carousel reads these arrays
  // instead so each frame gets its own copy.
  const [productNames, setProductNames] = useState<string[]>([]);
  const [productDescriptions, setProductDescriptions] = useState<string[]>([]);
  // Social Post route — transparent-product PNG produced by rembg on
  // the Confirm cutout step. Data URL ('data:image/png;base64,...').
  // Null until the user reaches the cutout step (the auto-run there
  // populates it). Cleared on every new upload via handleUpload.
  // SocialFinal composites this client-side onto the design's slot
  // color, skipping the OpenAI compose call.
  const [productTransparentImage, setProductTransparentImage] = useState<string | null>(null);

  // "Let AI design" vs "Choose a template" fork in the design step.
  // Defaults to 'ai' — the new path is the primary UX. Users can flip
  // to 'template' on the design step's tab bar to keep the legacy flow.
  // Drives:
  //   - which content renders at the 'social-design' / 'background' step
  //   - whether the terminal step shows AI result viewer or the template
  //     post (StepSocialFinal / StepPreview)
  //   - whether the 'text' step is skipped (AI mode bakes text in;
  //     no overlay step needed)
  // designMode default: 'ai' since the Creative Direction filters
  // landed (June 2, 2026). Prior to filters this defaulted to 'template'
  // because AI output was unpredictable; with 5 filters the user now
  // has real creative control so AI is the natural starting point.
  // Reverting this single line restores the prior 'template' default.
  const [designMode, setDesignMode] = useState<DesignMode>('ai');

  // Creative Direction filter state for the AI Design tab. Defaults are
  // opinionated so first-time users get a strong result without touching
  // anything. Persists across back-nav (state lives here, not in the
  // panel) and survives a designMode flip back to Template (so toggling
  // doesn't reset their choices).
  const [creativeDirection, setCreativeDirection] = useState<CreativeDirection>({
    mood: 'Premium',
    light: 'Soft daylight',
    background: 'Lifestyle',
    framing: 'Centered product',
    brand_presence: 'Balanced',
    logo: 'On',
    // text_density default matches today's pre-filter behavior so existing
    // users see no drift. Only shown in the UI for the Product Shot route
    // (gated via ProductShotAiExplainer's showTextDensity prop).
    text_density: 'Text + CTA',
  });
  // Result of the last wizardAiGenerate call. frames[0] is the rendered
  // PNG (data URL form); for carousel routes frames has 4 entries in
  // beat order. Cleared on new product upload / new run.
  const [aiResult, setAiResult] = useState<WizardAiGenerateResult | null>(null);
  // True while the wizardAiGenerate call is in flight. Parent owns this
  // (not AiGeneratePanel) so the terminal step (social-final) can render
  // the loading skeleton — the moment user clicks Generate, we advance
  // to the terminal step and IT shows the loader. Same UX pattern as
  // Ad Strategist's Step 5 → Step 6 advance.
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // ---------- Derived state ----------
  const isFashionRoute = postType === 'fashion';
  const isProductSwapRoute = postType === 'product-swap';
  const isSocialRoute = postType === 'single';
  const isCarouselRoute = postType === 'carousel';
  // Routes that expose the "Let AI design" fork at the design step.
  // Fashion + Product Swap stay template-only (mask-based; AI freestyle
  // doesn't fit those flows).
  const hasDesignFork = isSocialRoute || isCarouselRoute || (!isFashionRoute && !isProductSwapRoute);
  // postType → wizardAiGenerate route. Backend accepts any of the 4.
  const aiRoute: 'single' | 'carousel' | 'product-shot' | 'social-post' =
    isCarouselRoute ? 'carousel' : isSocialRoute ? 'social-post' : 'product-shot';
  // Section list — derived from postType, with one AI-mode override:
  // when designMode === 'ai' on a forked route, the 'text' step gets
  // trimmed (text is baked into the AI render — no overlay step needed).
  // Computed via useMemo so the Section accordion re-renders cleanly
  // when the user switches tabs.
  const sections = useMemo<readonly SectionId[]>(() => {
    const base = isFashionRoute
      ? FASHION_SECTIONS
      : isProductSwapRoute
        ? PRODUCT_SWAP_SECTIONS
        : isSocialRoute
          ? SOCIAL_SECTIONS
          : isCarouselRoute
            ? CAROUSEL_SECTIONS
            : DEFAULT_SECTIONS;
    if (hasDesignFork && designMode === 'ai' && base.includes('text')) {
      return base.filter((s) => s !== 'text');
    }
    return base;
  }, [isFashionRoute, isProductSwapRoute, isSocialRoute, isCarouselRoute, hasDesignFork, designMode]);

  const platformObj =
    socialPlatforms.find((p) => p.id === platform) ??
    productShotFormats.find((p) => p.id === platform) ??
    socialPostFormats.find((p) => p.id === platform) ??
    lockedSquareFormats.find((p) => p.id === platform);
  // Fashion + Product Swap are 1:1-locked in v1 (model/scene templates
  // are hand-authored at 1024×1024). The Format step exists to surface
  // the roadmap; aspect itself is always 1:1 regardless of what the
  // user clicks (and only 1:1 is `available: true` anyway).
  const platformAspectRatio = (isFashionRoute || isProductSwapRoute) ? '1:1' : (platformObj?.aspectRatio || '1:1');
  // The backend templateId differs per route — Product Swap uses the scene
  // id, Fashion the model id, everything else the background scene id.
  const activeTemplateId = isProductSwapRoute
    ? (productSwapSceneId || '')
    : isFashionRoute
      ? (fashionModelId || '')
      : (sceneId || '');
  // Badge-driven so adding new carousel platforms doesn't require code changes.
  const isCarousel = (platformObj as { badge?: string } | undefined)?.badge === 'Multi';
  // Carousel route allows up to 4 products (one per frame per the
  // cyclic mapping spec — see StepPreview's mapFrameToProduct).
  // Other routes are single-product as before.
  const maxImages = isCarouselRoute ? 4 : 1;

  // ---------- AI Generate auto-fire on Preview step ----------
  // For Product Shot + Carousel routes in AI mode: when the user lands
  // on the 'preview' step and we don't have a result yet, fire the
  // wizardAiGenerate call automatically. Same UX as the template path
  // (StepPreview auto-fires /api/generate on entry).
  //
  // Social Post is different — it has no preview step; the AI panel on
  // social-design owns its own Generate button.
  const handleAiGenerateAutoFire = useCallback(async () => {
    const productFile = productFiles[0];
    if (!productFile) return;
    setAiError(null);
    setAiGenerating(true);
    try {
      const result = await wizardAiGenerate({
        product: productFile,
        route: aiRoute,
        aspectRatio: platformAspectRatio,
        quality,
        productName,
        productDescription,
        creativeDirection,
      });
      setAiResult(result);
      refreshCredits();
    } catch (err) {
      const msg = err instanceof InsufficientCreditsError
        ? (err.message || 'Not enough credits. Upgrade or top up.')
        : (err instanceof Error ? err.message : 'AI Generate failed. Click Retry.');
      setAiError(msg);
    } finally {
      setAiGenerating(false);
    }
    // refreshCredits is from useCredits hook (stable reference)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFiles, aiRoute, platformAspectRatio, quality, productName, productDescription, creativeDirection]);

  // Trigger the auto-fire when the user enters the Preview step in AI
  // mode and there's no result yet (and nothing already in flight).
  // sections is reactive to designMode, so this re-evaluates correctly
  // when the user toggles tabs at the design step.
  useEffect(() => {
    if (!hasDesignFork || isSocialRoute) return; // Social fires from its panel
    if (designMode !== 'ai') return;
    if (aiGenerating || aiResult) return;
    if (aiError) return; // Don't auto-refire after a failure — user clicks Retry
    const currentSection = sections[step - 1];
    if (currentSection !== 'preview') return;
    if (!productFiles[0]) return;
    void handleAiGenerateAutoFire();
  }, [step, sections, designMode, hasDesignFork, isSocialRoute, aiGenerating, aiResult, aiError, productFiles, handleAiGenerateAutoFire]);

  // ---------- Section state derivation ----------
  const stateOf = (n: number): 'locked' | 'active' | 'completed' => {
    if (step === n) return 'active';
    if (step > n) return 'completed';
    return 'locked';
  };

  const editAt = (n: number) => () => setStep(n);

  // ---------- Handlers ----------
  const handleCostAdd = useCallback((delta: number) => {
    setCostSpent((prev) => prev + delta);
  }, []);

  // Category switching is no longer possible mid-wizard — postType is
  // locked by the URL param on mount (see initialPostType above). If the
  // user wants to switch, they go back to /create. Cross-route state
  // leakage is impossible since each /create click is a fresh page load.
  //
  // Mount-time guard: if ?type= was missing or invalid, redirect to
  // /create. This runs in a useEffect (can't navigate during render).
  useEffect(() => {
    if (initialPostType === null) {
      navigate('/create', { replace: true });
    }
  }, [initialPostType, navigate]);

  // Fetch the My Products library once on mount so the Upload section
  // can show the picker pane. Failures degrade silently — the URL +
  // manual-upload paths still work even if the library can't load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listProducts();
        if (!cancelled) setSavedProducts(list);
      } catch (err) {
        console.error('Failed to load saved products:', err);
        if (!cancelled) setSavedProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // Fetch the active brand on mount + brand-switch so StepText's Add Logo
  // affordance can offer a one-click "stamp my logo on this frame" action.
  // Null when the user hasn't set up a brand yet — StepText shows a graceful
  // "Set up in My Brand →" inline prompt instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const brand = await getBrand();
        if (!cancelled) setBrandLogoUrl(brand?.logoUrl ?? null);
      } catch (err) {
        console.error('Failed to load brand:', err);
        if (!cancelled) setBrandLogoUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // One-shot read of an Ad Strategist concept handoff. Pre-selects the
  // matched template for the route and stashes the productId for the
  // product-pick effect below. Cleared immediately so it doesn't persist
  // across unrelated wizard visits.
  const pendingProductIdRef = useRef<string | null>(null);
  const handoffProductAppliedRef = useRef(false);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('adStrategistConcept');
      if (!raw) return;
      sessionStorage.removeItem('adStrategistConcept');
      const payload = JSON.parse(raw);
      setAdConcept(payload);
      if (payload.templateId) {
        if (payload.route === 'product-swap') setProductSwapSceneId(payload.templateId);
        else if (payload.route === 'single') setSocialDesignId(payload.templateId);
        else setSceneId(payload.templateId); // product-shot / carousel
      }
      pendingProductIdRef.current = payload.productId || null;
    } catch { /* ignore */ }
  }, []);

  const handlePlatformSelect = useCallback((id: string) => {
    setPlatform(id);
  }, []);

  // Upload — clear auto-detected fields so the next visit to Details
  // re-runs analyze-product against the fresh file. Without this clear,
  // a user who uploads, then swaps in a different product would see
  // stale garmentType + recommendations from the prior product.
  const handleUpload = useCallback((images: string[], files: File[]) => {
    setProductImages(images);
    setProductFiles(files);
    // Wipe ALL state derived from the previous product so a fresh upload
    // doesn't leak stale data downstream — see /app/page.tsx handleUpload
    // for the full rationale (cache-key collisions, StepDetails skip-guard,
    // StepSocialFinal composedProductUrl staleness).
    setProductName('');
    setProductDescription('');
    setGarmentType(null);
    setGarmentRestriction(null);
    setHasHumanModel(null);
    setProductCategory(null);
    setProductTags([]);
    setProductSilhouette(null);
    setGeneratedImages([]);
    setSuggestedFrames([]);
    setLastGenKey(null);
    setSocialSlots(null);
    setTextOverlays([]);
    setShapeOverlays([]);
    setLogoOverlays([]);
    setSelectedPresetId(null);
    // Clear library selection by default. The library-pick handler
    // calls handleUpload first, then re-sets the selected id AFTER —
    // so this clear only persists for URL-scrape + manual-file paths.
    setSelectedSavedProductId(null);
    setSelectedSavedProductIds([]);
    setProductNames([]);
    setProductDescriptions([]);
    // Social Post cutout — fresh upload means the old cutout is
    // invalid. The Confirm-cutout step will re-run rembg when
    // entered with productFile present and transparentImage null.
    setProductTransparentImage(null);
    // AI Generate result — tied to the previous product, no longer valid.
    setAiResult(null);
  }, []);

  // Multi-product carousel — append a new product without wiping the
  // existing array (unlike handleUpload which is a full replace +
  // state wipe). Called by StepUpload's URL-scrape + file-upload
  // paths when maxImages > 1.
  // Optional `name` / `description` parameters pre-populate the
  // per-product metadata arrays — URL-scrape passes them via the
  // onScrapeSuccess wrapper; library-pick passes them via the
  // carousel branch in handleSavedProductPick; manual file uploads
  // pass undefined, so empty strings are appended (StepDetails'
  // multi-product cards run analyze on each empty entry).
  const handleAddProduct = useCallback((
    image: string,
    file: File,
    name?: string,
    description?: string,
  ) => {
    setProductImages((prev) => [...prev, image]);
    setProductFiles((prev) => [...prev, file]);
    setProductNames((prev) => [...prev, name ?? '']);
    setProductDescriptions((prev) => [...prev, description ?? '']);
    // Invalidate downstream cached state since a new product changes
    // the per-frame mapping.
    setGeneratedImages([]);
    setSuggestedFrames([]);
    setLastGenKey(null);
    setAiResult(null);
  }, []);

  const handleRemoveProduct = useCallback((idx: number) => {
    setProductImages((prev) => prev.filter((_, i) => i !== idx));
    setProductFiles((prev) => prev.filter((_, i) => i !== idx));
    setProductNames((prev) => prev.filter((_, i) => i !== idx));
    setProductDescriptions((prev) => prev.filter((_, i) => i !== idx));
    setSelectedSavedProductIds((prev) => prev.filter((_, i) => i !== idx));
    setGeneratedImages([]);
    setSuggestedFrames([]);
    setLastGenKey(null);
    setAiResult(null);
  }, []);

  // Update name/description for a specific carousel product slot.
  // Bound to StepDetails' per-card onChange callbacks.
  const handleProductNameAt = useCallback((idx: number, value: string) => {
    setProductNames((prev) => prev.map((n, i) => (i === idx ? value : n)));
    setLastGenKey(null);
  }, []);
  const handleProductDescriptionAt = useCallback((idx: number, value: string) => {
    setProductDescriptions((prev) => prev.map((d, i) => (i === idx ? value : d)));
    setLastGenKey(null);
  }, []);

  // Multi-product carousel — deselect a saved product (toggle off).
  // Removes the entry from both the selection-id array AND the
  // products array at the matching position.
  const handleSavedProductDeselect = useCallback((product: UserProduct) => {
    setSelectedSavedProductIds((prev) => {
      const idx = prev.indexOf(product.id);
      if (idx < 0) return prev;
      // Mirror the removal in productImages + productFiles via
      // handleRemoveProduct (which also nukes cached gen state).
      handleRemoveProduct(idx);
      return prev.filter((_, i) => i !== idx);
    });
  }, [handleRemoveProduct]);

  // Picked from My Products library — download the public image into a
  // File and populate name + description + classification fields. Does
  // NOT auto-advance — the user explicitly clicks Continue (per the
  // design req: "It should never advance automatically"). Pre-fill is
  // enough to make Details a one-glance step (its skip-guard already
  // bypasses analyze-product when name + description are present).
  const handleSavedProductPick = useCallback(async (product: UserProduct) => {
    if (!product.imageUrl) {
      throw new Error('This saved product has no image.');
    }
    // Fetch the public image as a Blob → File. Public Supabase buckets
    // allow cross-origin GET so this works from any origin.
    const resp = await fetch(product.imageUrl);
    if (!resp.ok) {
      throw new Error(`Could not download the saved image (HTTP ${resp.status}).`);
    }
    const blob = await resp.blob();
    // Use a unique filename so downstream caches keyed on file.name
    // (StepPreview's cache, StepSocialFinal's composedKeyRef) don't
    // collide between two saved-product picks of the same library item.
    const filename = `saved-${product.id.slice(0, 8)}-${Date.now()}.jpg`;
    const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
    // Carousel route (multi-product): APPEND to the existing arrays
    // instead of wiping them. Pass the saved product's name +
    // description so they pre-populate the per-product metadata
    // arrays — avoids hitting analyze-product on a library pick that
    // already has cached metadata.
    if (isCarouselRoute) {
      handleAddProduct(dataUrl, file, product.name ?? '', product.description ?? '');
      setSelectedSavedProductIds((prev) => [...prev, product.id]);
      // For Background recommendations we use the FIRST product's
      // category + tags (single source of truth for "which scenes
      // suit these products"). Only set if not already populated.
      if (!productName) setProductName(product.name || '');
      if (!productDescription) setProductDescription(product.description || '');
      if (!productCategory && product.productCategory) setProductCategory(product.productCategory);
      if (productTags.length === 0 && product.tags && product.tags.length > 0) {
        setProductTags(product.tags);
      }
      return;
    }
    // Single-product route — original wipe-then-pre-fill path.
    // handleUpload wipes name/desc/etc (including selectedSavedProductId)
    // — call it FIRST, then set the saved metadata + selection on top.
    // Otherwise the wipe would clobber both the pre-fill AND the
    // selected-tile visual state.
    handleUpload([dataUrl], [file]);
    setProductName(product.name || '');
    setProductDescription(product.description || '');
    if (product.productCategory) setProductCategory(product.productCategory);
    if (product.tags && product.tags.length > 0) setProductTags(product.tags);
    setSelectedSavedProductId(product.id);
  }, [handleUpload, handleAddProduct, isCarouselRoute, productName, productDescription, productCategory, productTags]);

  // Pre-pick a handed-off Ad Strategist product once the library loads.
  // Placed after handleSavedProductPick so its deps array isn't in the TDZ.
  // handleSavedProductPick wipes derived state but NOT the template selection
  // applied in the handoff-read effect above, so order is safe.
  useEffect(() => {
    if (!savedProducts || handoffProductAppliedRef.current) return;
    const pid = pendingProductIdRef.current;
    if (!pid) return;
    const prod = savedProducts.find((p) => p.id === pid);
    if (prod) {
      handoffProductAppliedRef.current = true;
      handleSavedProductPick(prod);
    }
  }, [savedProducts, handleSavedProductPick]);

  const handleSceneSelect = useCallback((id: string) => {
    setSceneId(id);
  }, []);

  const handleQualitySelect = useCallback((q: Quality) => {
    setQuality(q);
  }, []);

  // Continue handlers — each gates advance on the section's own validity.
  // Step number is the section's position in the current route's list,
  // so "next" = step + 1.
  const handlePlatformContinue   = () => { if (platform) setStep(step + 1); };
  const handleUploadContinue     = () => { if (productFiles.length > 0) setStep(step + 1); };
  // Fashion route: gate on a real garment classification (≠ 'none') AND
  // no restricted-content flag.
  // Product Shot route: block when a person is detected — those uploads
  // belong in Fashion. Carousel + Social Post are more permissive.
  // Default route just needs productName.
  // Multi-product carousel: gate on ALL product names being non-empty
  // (a missing name on any single product would yield a bad copy /
  // image gen for that frame). Empty descriptions are allowed since
  // they're optional.
  const isMultiProductCarousel = isCarouselRoute && productFiles.length > 1;
  const allMultiNamesFilled = isMultiProductCarousel
    && productNames.length === productFiles.length
    && productNames.every((n) => n.trim().length > 0);
  const handleDetailsContinue    = () => {
    if (isMultiProductCarousel) {
      if (!allMultiNamesFilled) return;
    } else if (productName.trim().length === 0) {
      return;
    }
    if (isFashionRoute) {
      if (!garmentType || garmentType === 'none') return;
      if (garmentRestriction && garmentRestriction !== 'none') return;
    }
    if (postType === 'product-shot' && hasHumanModel === true) return;
    setStep(step + 1);
  };
  const handleConfirmCutoutContinue = () => {
    if (productTransparentImage) setStep(step + 1);
  };
  const handleBackgroundContinue = () => {
    // AI mode doesn't need a sceneId — it's a freestyle generate.
    // Template mode requires the user to pick a scene first.
    if (designMode === 'ai') { setStep(step + 1); return; }
    if (sceneId) setStep(step + 1);
  };
  const handleFashionModelContinue = () => { if (fashionModelId) setStep(step + 1); };
  const handleProductSwapSceneContinue = () => { if (productSwapSceneId) setStep(step + 1); };
  const handleSocialDesignContinue = () => { if (socialDesignId) setStep(step + 1); };
  const handleQualityContinue    = () => setStep(step + 1);

  const handleGenerationComplete = useCallback(
    (images: string[], frames: SuggestedFrame[], key: string) => {
      setGeneratedImages(images);
      setSuggestedFrames(frames);
      setLastGenKey(key);
    },
    [],
  );

  // Step Preview → Add Text & Logo advance. Section index is route-dependent
  // (default: Preview=7, AddText=8; fashion: Preview=6, AddText=7).
  const handlePreviewContinue = () => setStep(step + 1);

  const handleRegenerateCopy = useCallback(async (): Promise<SuggestedFrame[] | null> => {
    const file = productFiles[0];
    if (!file) return null;
    try {
      const result = await suggestCopy({
        productFile: file,
        productName,
        productDescription,
        frameCount: isCarousel ? 4 : 1,
      });
      setSuggestedFrames(result.frames);
      handleCostAdd(result._cost ?? 0);
      return result.frames;
    } catch (err) {
      console.error('Regenerate copy failed:', err);
      return null;
    }
  }, [productFiles, productName, productDescription, isCarousel, handleCostAdd]);

  // Has the user made any progress that would be lost by leaving / resetting?
  // Mirrors /app's check. Used to decide whether to surface a confirmation
  // modal or perform the destructive action immediately.
  const hasProgress =
    !!postType ||
    !!platform ||
    productImages.length > 0 ||
    !!productName.trim() ||
    !!sceneId ||
    !!fashionModelId ||
    !!productSwapSceneId ||
    !!socialDesignId ||
    generatedImages.length > 0 ||
    textOverlays.length > 0 ||
    logoOverlays.length > 0 ||
    !!aiResult;

  // performReset (in-place state wipe) was removed May 26. Wizard reset
  // is now implicit — navigating to /create unmounts this page and the
  // next /appv2 visit starts with fresh state.

  // New Creative — navigates to /create. Prompts for confirmation if there's
  // unsaved progress; otherwise navigates immediately.
  const handleNewPost = () => {
    if (hasProgress) {
      setShowNewPostConfirm(true);
    } else {
      navigate('/create');
    }
  };
  const confirmNewPost = () => {
    setShowNewPostConfirm(false);
    navigate('/create');
  };
  const cancelNewPost = () => setShowNewPostConfirm(false);

  // Gallery — same confirmation pattern. Navigates away on confirm.
  const handleGallery = () => {
    if (hasProgress) {
      setShowExitConfirm(true);
    } else {
      navigate('/gallery');
    }
  };
  const confirmGallery = () => {
    setShowExitConfirm(false);
    navigate('/gallery');
  };
  const cancelGallery = () => setShowExitConfirm(false);

  // Shared Continue button — every Section uses the same shape.
  // Microinteractions: subtle lift + shadow on hover, gentle press-down
  // on active. Arrow nudges right to telegraph forward motion.
  const continueBtn = (onClick: () => void, disabled: boolean) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="
        group inline-flex items-center gap-2 px-6 py-3 rounded-full
        bg-primary hover:bg-primary-hover text-white text-sm font-bold
        transition-all duration-200 cursor-pointer
        shadow-md shadow-primary/25
        hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5
        active:translate-y-0 active:shadow-md
        disabled:opacity-50 disabled:cursor-not-allowed
        disabled:hover:translate-y-0 disabled:hover:shadow-md
      "
    >
      Continue
      <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-0.5 group-disabled:group-hover:translate-x-0" />
    </button>
  );

  // ---------- Per-section summaries (collapsed completed rows) ----------
  const platformSummary = platformObj
    ? `${platformObj.name} · ${platformObj.aspectRatio} · ${platformObj.dimensions}`
    : '';
  const uploadSummary = productFiles[0] ? productFiles[0].name : '';
  const detailsSummary = productName.trim() || '';
  const backgroundSummary = sceneId ? sceneId : '';
  const fashionModelSummary = fashionModelId ? fashionModelId : '';
  const productSwapSceneSummary = productSwapSceneId ? productSwapSceneId : '';
  const socialDesignSummary = socialDesignId ? socialDesignId : '';
  const qualitySummary = (() => {
    const mult = quality === 'low' ? 1 : quality === 'medium' ? 2 : 4;
    const frames = isCarousel ? 4 : 1;
    return `${quality.charAt(0).toUpperCase() + quality.slice(1)} · ${mult * frames} credit${
      mult * frames === 1 ? '' : 's'
    }`;
  })();
  const previewSummary = generatedImages.length > 0
    ? `${generatedImages.length} image${generatedImages.length === 1 ? '' : 's'} generated`
    : '';

  // ---------- Save-as-Recreate (dev) ----------
  // Wired into StepText + StepSocialFinal's `onSaveAsRecreate` prop. Child
  // captures the current full-resolution composited PNG and hands it up
  // here. We prompt for a name, slugify it, assemble a RecreateTemplate
  // from the wizard state, and POST to the Vite middleware which writes
  // the PNG + upserts the template entry in recreate-templates.json.
  //
  // Dev-only — the middleware doesn't exist in production. Best-effort:
  // if the network call fails (e.g. running against a deployed backend
  // without the Vite dev server) the user sees an alert and nothing else.
  const handleSaveAsRecreate = useCallback(async (previewPngBase64: string) => {
    if (!postType) {
      alert('Cannot save: no category selected.');
      return;
    }
    const defaultName = (productName.trim() || 'Untitled template').slice(0, 60);
    const name = window.prompt(
      'Recreate template name (shown on the dashboard tile):',
      defaultName,
    );
    if (!name || !name.trim()) return;  // user cancelled

    // Slugify to a kebab-case id. Strip non-alphanumerics, collapse spaces,
    // append a 4-char random suffix to dodge collisions when iterating on
    // the same product. Middleware enforces the regex /^[a-z0-9-]+$/.
    const slugBase = name.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
    const suffix = Math.random().toString(36).slice(2, 6);
    const id = `${slugBase}-${suffix}`;

    // Derive the backend templateId. The wizard's state holds different
    // values per route — unify here so RecreateTemplate.templateId is
    // always the right shape for /api/generate.
    let templateId: string;
    if (isSocialRoute) {
      // Social Post uses the `social-bg-<designId>` dispatch on the
      // backend. designId == socialDesignId.
      templateId = `social-bg-${socialDesignId ?? 'editorial'}`;
    } else if (isFashionRoute) {
      templateId = fashionModelId ?? '';
    } else if (isProductSwapRoute) {
      templateId = productSwapSceneId ?? '';
    } else {
      // product-shot / carousel
      templateId = sceneId ?? '';
    }
    if (!templateId) {
      alert('Cannot save: no template / scene / model selected.');
      return;
    }

    // Resolve the platformId. Carousel route auto-set platform on mount;
    // fashion route doesn't pick one (locked 1:1 — use 'instagram' as the
    // recreate-runtime platformId so aspect-ratio lookup works).
    const platformId = platform ?? ((isFashionRoute || isProductSwapRoute) ? 'instagram' : '');
    if (!platformId) {
      alert('Cannot save: no platform selected.');
      return;
    }

    // Assemble the RecreateTemplate. Per-route fields populated conditionally
    // so the JSON doesn't carry irrelevant noise (e.g. textOverlays on a
    // Social Post template).
    const template = {
      id,
      name: name.trim(),
      category: postType,
      platformId,
      templateId,
      quality,
      provider,
      ...(isFashionRoute && garmentType && garmentType !== 'none'
        ? { garmentType }
        : {}),
      ...(isSocialRoute && socialDesignId
        ? { socialDesignId }
        : {}),
      ...(!isSocialRoute && textOverlays.length > 0
        ? { textOverlays }
        : {}),
      // The middleware overwrites previewImage to the just-written path,
      // but we set a placeholder here to satisfy the type.
      previewImage: `/recreate-previews/${id}.png`,
    };

    try {
      const resp = await fetch('/api/recreate-templates-save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template, previewPngBase64 }),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json.error ?? `HTTP ${resp.status}`);
      }
      // Navigate to /create so the user sees the new tile. The dashboard's
      // Vite HMR picks up the JSON change so the new entry appears
      // immediately without a manual refresh.
      navigate('/create');
    } catch (err) {
      console.error('Save as Recreate failed:', err);
      alert(
        `Failed to save Recreate template: ${
          err instanceof Error ? err.message : 'Unknown error'
        }\n\n` +
          'Note: this is a DEV-ONLY tool — it requires the Vite dev server. ' +
          'It will not work against a deployed backend.',
      );
      // Re-throw so the child can flip its button state to error.
      throw err;
    }
  }, [
    postType,
    productName,
    isSocialRoute,
    isFashionRoute,
    isProductSwapRoute,
    socialDesignId,
    fashionModelId,
    productSwapSceneId,
    sceneId,
    platform,
    quality,
    provider,
    garmentType,
    textOverlays,
    navigate,
  ]);

  // ---------- Section renderer ----------
  // One JSX block per SectionId. The map below pulls the right block by
  // its position in the current route's list.
  const renderSection = (id: SectionId, idx: number) => {
    const stepNum = idx + 1;
    const state = stateOf(stepNum);

    switch (id) {
      case 'platform':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Platform"
            icon="ri-smartphone-line"
            description="Pick the social format. Each has its own dimensions and aspect ratio."
            state={state}
            summary={platformSummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handlePlatformContinue, !platform)}
          >
            <StepPlatform
              selected={platform}
              onSelect={handlePlatformSelect}
              postType={postType}
              compact
            />
          </Section>
        );

      case 'upload':
        return (
          <Section
            key={id}
            step={stepNum}
            title={isFashionRoute ? 'Garment selection' : 'Product selection'}
            icon="ri-upload-cloud-line"
            description={
              isFashionRoute
                ? 'Pick a saved product from your library on the left, or upload a clear photo of the garment on the right.'
                : 'Pick a saved product from your library on the left, or add a new one on the right.'
            }
            state={state}
            summary={uploadSummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleUploadContinue, productFiles.length === 0)}
          >
            <StepUpload
              uploadedImages={productImages}
              maxImages={maxImages}
              onUpload={handleUpload}
              platformId={platform || 'instagram'}
              onScrapeSuccess={(name, description, insights) => {
                // URL-scrape path: backend pre-fills name/description from
                // OG/JSON-LD parsing AND productCategory + tags from a cheap
                // text-only LLM call (~$0.0001). Pre-fill all of it so
                // Details can skip the more expensive vision analyze-product
                // call, AND Background has insights ready for "Recommended
                // for you" without any extra work.
                setProductName(name);
                setProductDescription(description);
                if (insights?.productCategory) setProductCategory(insights.productCategory);
                if (insights?.tags && insights.tags.length > 0) setProductTags(insights.tags);
              }}
              // Library mode: pass the saved-products list + pick handler.
              // While the initial fetch is in flight (savedProducts === null)
              // we pass [] so the left pane shows its empty-state UI rather
              // than nothing — feels less broken than waiting for the
              // network. tabsMode renders Library/URL as a tab switcher
              // (matches the recreate page convention; consistent UX
              // across both surfaces).
              savedProducts={savedProducts ?? []}
              savedProductsLoading={savedProducts === null}
              onSavedProductSelect={handleSavedProductPick}
              selectedSavedProductId={selectedSavedProductId}
              // Carousel route adds multi-select props. Non-carousel
              // routes leave these undefined so StepUpload falls back
              // to its single-select code paths.
              selectedSavedProductIds={isCarouselRoute ? selectedSavedProductIds : undefined}
              onSavedProductDeselect={isCarouselRoute ? handleSavedProductDeselect : undefined}
              onAddProduct={isCarouselRoute ? handleAddProduct : undefined}
              onRemoveProduct={isCarouselRoute ? handleRemoveProduct : undefined}
              tabsMode
              // Fashion-route gate: only saved products tagged as
              // category 'fashion' are selectable. Non-fashion items
              // stay visible (so users understand the gate) but are
              // greyscaled + carry a "Not for fashion" ribbon label
              // (replaces the prior lock icon — explicit text reads
              // at a glance without requiring a hover).
              isSavedProductDisabled={
                isFashionRoute
                  ? (p) => p.productCategory !== 'fashion'
                  : undefined
              }
              disabledReason={
                isFashionRoute
                  ? "This isn't a fashion item — pick a saved garment, or upload a new garment photo on the next tab."
                  : undefined
              }
              disabledLabel={isFashionRoute ? 'Not for fashion' : undefined}
              compact
            />
          </Section>
        );

      case 'confirm-cutout':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Crop your product"
            icon="ri-crop-line"
            description="Trim to the product so it fills the design. Cropping is optional — we place it cleanly into the template either way."
            state={state}
            summary={productTransparentImage ? 'Cropped' : ''}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleConfirmCutoutContinue, !productTransparentImage)}
          >
            <StepConfirmCutout
              productFile={productFiles[0] || null}
              productImage={productImages[0] || ''}
              croppedImage={productTransparentImage}
              onCutoutChange={setProductTransparentImage}
              compact
            />
          </Section>
        );

      case 'details':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Product details"
            icon="ri-sparkling-2-line"
            description="AI fills these in from your photo. Edit anything that's off."
            state={state}
            summary={detailsSummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(
              handleDetailsContinue,
              isMultiProductCarousel
                ? !allMultiNamesFilled
                : (productName.trim().length === 0
                  || (isFashionRoute && (!garmentType || garmentType === 'none'))
                  || (isFashionRoute && !!garmentRestriction && garmentRestriction !== 'none')
                  || (postType === 'product-shot' && hasHumanModel === true)),
            )}
          >
            <StepDetails
              productFile={productFiles[0] || null}
              productImage={productImages[0] || ''}
              productName={productName}
              productDescription={productDescription}
              onProductNameChange={setProductName}
              onProductDescriptionChange={setProductDescription}
              // Fashion route — surface auto-detected garment + content
              // restriction so the warning + continue gate work.
              onGarmentTypeDetected={setGarmentType}
              onGarmentRestrictionDetected={setGarmentRestriction}
              requireGarment={isFashionRoute}
              garmentType={garmentType}
              garmentRestriction={garmentRestriction}
              // Product-shot route — soft-warn when an uploaded photo
              // includes a human/model (those belong in Fashion).
              onHumanModelDetected={setHasHumanModel}
              warnHumanModel={postType === 'product-shot'}
              hasHumanModel={hasHumanModel}
              // All routes — capture analyze-product's productCategory +
              // tags so StepBackground's "Recommended for you" can rank.
              onProductInsightsDetected={({ category, tags, silhouette }) => {
                if (category) setProductCategory(category);
                if (tags) setProductTags(tags);
                if (silhouette) setProductSilhouette(silhouette);
              }}
              onCostAdd={handleCostAdd}
              compact
              // Multi-product carousel: pass the parallel arrays so
              // StepDetails renders the per-product card stack instead
              // of the single-product two-column layout. Falls back
              // to single-product when productFiles.length <= 1.
              multiProducts={isMultiProductCarousel ? {
                files: productFiles,
                images: productImages,
                names: productNames,
                descriptions: productDescriptions,
                onNameChange: handleProductNameAt,
                onDescriptionChange: handleProductDescriptionAt,
              } : undefined}
            />
          </Section>
        );

      case 'background': {
        // Two paths, same as Social Post's social-design step:
        //   AI       → DesignStep AI tab = explainer card. Generate
        //              fires later on the Preview step (Quality picks
        //              the credit tier first).
        //   Template → existing StepBackground picker.
        // Continue advances regardless of mode — AI needs Quality next.
        const aiReady = designMode === 'ai';
        const templateReady = designMode === 'template' && !!sceneId;
        return (
          <Section
            key={id}
            step={stepNum}
            title="Choose a design"
            icon="ri-palette-line"
            description={
              designMode === 'ai'
                ? 'AI designs the whole ad — scene, layout, copy, CTA — from your brand + product. Pick quality next, then your ad renders on Preview.'
                : "The scene we'll place your product into."
            }
            state={state}
            summary={
              designMode === 'ai'
                ? 'AI-designed'
                : backgroundSummary
            }
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleBackgroundContinue, !(aiReady || templateReady))}
          >
            <DesignStep
              mode={designMode}
              onModeChange={(m) => {
                setDesignMode(m);
                // Switching modes invalidates the OTHER path's artifact.
                if (m === 'ai') {
                  // Keep sceneId — user might toggle back to Template.
                } else {
                  setAiResult(null);
                  setAiError(null);
                }
              }}
              aiContent={
                // For Product Shot / Carousel the AI panel is a pure
                // explainer — no Generate button (the call fires when
                // user lands on Preview). The brand-context callout +
                // bullet list keep the design step useful even without
                // a CTA: the user knows what they're paying for before
                // continuing.
                <ProductShotAiExplainer
                  route={aiRoute}
                  creativeDirection={creativeDirection}
                  onCreativeDirectionChange={setCreativeDirection}
                />
              }
              templateContent={
                <StepBackground
                  selected={sceneId}
                  onSelect={handleSceneSelect}
                  productCategory={productCategory ?? undefined}
                  productTags={productTags}
                  compact
                />
              }
            />
          </Section>
        );
      }

      case 'fashion-model':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Choose a model"
            icon="ri-user-line"
            description="Pick a model template. We'll composite your garment onto the matching region."
            state={state}
            summary={fashionModelSummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleFashionModelContinue, !fashionModelId)}
          >
            <StepFashionModel
              selected={fashionModelId}
              onSelect={setFashionModelId}
              compact
            />
          </Section>
        );

      case 'product-swap-scene':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Choose a scene"
            icon="ri-hand-heart-line"
            description="Pick a shot where a model holds a product. We'll swap your product into their hand."
            state={state}
            summary={productSwapSceneSummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleProductSwapSceneContinue, !productSwapSceneId)}
          >
            <StepProductSwapScene
              selected={productSwapSceneId}
              onSelect={setProductSwapSceneId}
              productSilhouette={productSilhouette}
              compact
            />
          </Section>
        );

      case 'social-design': {
        // Two paths at this step:
        //   - AI: "Let AI design" tab — user configures (optional headline
        //     hint + CTA) then clicks Generate inside the AI panel. On
        //     success we lift the result up + auto-advance to social-final
        //     (which renders the AI viewer when designMode === 'ai').
        //   - Template: existing StepSocialDesign — pick one of 8 JSON
        //     templates, advance to social-final (which renders the
        //     existing JsonSocialPost composite).
        //
        // Continue button is only relevant on the Template path — AI
        // auto-advances on Generate. Disabled state shifts accordingly.
        const aiReady = designMode === 'ai' && !!aiResult;
        const templateReady = designMode === 'template' && !!socialDesignId;
        const handleContinue = () => {
          if (designMode === 'ai') {
            // Don't bother — onAiResult below auto-advances.
            return;
          }
          handleSocialDesignContinue();
        };
        return (
          <Section
            key={id}
            step={stepNum}
            title="Choose a design"
            icon="ri-layout-grid-line"
            description={
              designMode === 'ai'
                ? 'AI designs the whole post — background, layout, copy, CTA — from your brand + product. Click Generate when ready.'
                : 'Pick a designed template. AI fills the copy fields based on your product on the next step.'
            }
            state={state}
            summary={
              designMode === 'ai'
                ? (aiResult ? 'AI-designed' : 'AI mode')
                : socialDesignSummary
            }
            onEdit={editAt(stepNum)}
            cta={designMode === 'template' ? continueBtn(handleContinue, !templateReady) : undefined}
          >
            <DesignStep
              mode={designMode}
              onModeChange={(m) => {
                setDesignMode(m);
                // Switching modes invalidates the other path's result.
                if (m === 'ai') {
                  // Keep socialDesignId untouched — user might switch back.
                } else {
                  setAiResult(null);
                }
              }}
              aiContent={
                <AiGeneratePanel
                  route="social-post"
                  productFile={productFiles[0] || null}
                  productName={productName}
                  productDescription={productDescription}
                  aspectRatio="1:1"
                  quality="low"
                  creativeDirection={creativeDirection}
                  onCreativeDirectionChange={setCreativeDirection}
                  onGenerateStart={() => {
                    // Advance to social-final IMMEDIATELY so the loading
                    // skeleton appears there. Result lands on success;
                    // error reverts the step.
                    setAiError(null);
                    setAiResult(null);
                    setAiGenerating(true);
                    setStep(step + 1);
                  }}
                  onResult={(result) => {
                    setAiResult(result);
                    setAiGenerating(false);
                    refreshCredits();
                  }}
                  onError={(message) => {
                    setAiGenerating(false);
                    setAiError(message);
                    // Bounce back to the design step so the error +
                    // Generate button are co-located for retry.
                    setStep(step);
                  }}
                />
              }
              templateContent={
                <StepSocialDesign
                  selected={socialDesignId}
                  onSelect={setSocialDesignId}
                  compact
                  brandLogoUrl={brandLogoUrl}
                />
              }
            />
            {/* AI-error surface — shown when the API call failed and
                we bounced the user back to this step. Sits inside the
                Section so it's visible regardless of which tab is open. */}
            {designMode === 'ai' && aiError && (
              <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5" />
                <div className="flex-1">{aiError}</div>
              </div>
            )}
            {/* AI-ready hint — when the user has already generated, point
                them at the result step. Quiet — the auto-advance handles
                most cases, this is just for re-entry from Edit. */}
            {designMode === 'ai' && aiReady && !aiError && (
              <p className="mt-3 text-xs text-dark-muted">
                ✓ AI ad generated. Continue to download or click "Try again" inside the AI panel to re-roll.
              </p>
            )}
          </Section>
        );
      }

      case 'social-final': {
        // Export dimensions from the platform pick on Section 2.
        // Defaults to 1080×1080 (Instagram square) when the lookup fails.
        const dimsParts = (platformObj?.dimensions ?? '1080 x 1080')
          .split('x')
          .map((s) => parseInt(s.trim(), 10));
        const exportWidth = dimsParts[0] || 1080;
        const exportHeight = dimsParts[1] || 1080;
        // AI mode: render the AI-baked ad + download / try again. Template
        // mode: render the existing StepSocialFinal (editable template
        // composite with text slots, save-to-gallery, etc.).
        if (designMode === 'ai') {
          return (
            <Section
              key={id}
              step={stepNum}
              title={aiGenerating ? 'Designing your ad…' : 'Your AI-designed ad'}
              icon="ri-sparkling-2-line"
              description={
                aiGenerating
                  ? 'AI is composing your ad. Stay on this step — the result will appear here in 15–20 seconds.'
                  : "Download the rendered ad below. Want a different take? Go back and click Generate again."
              }
              state={state}
            >
              <AiResultViewer
                result={aiResult}
                loading={aiGenerating}
                expectedFrameCount={1}
                onBack={() => setStep(step - 1)}
              />
            </Section>
          );
        }
        return (
          <Section
            key={id}
            step={stepNum}
            title="Your designed post"
            icon="ri-magic-line"
            description="Edit any field on the right. Download as PNG or save to your gallery when ready."
            state={state}
          >
            <StepSocialFinal
              productFile={productFiles[0] || null}
              productImageUrl={productImages[0] || ''}
              productName={productName}
              productDescription={productDescription}
              designId={socialDesignId || 'editorial'}
              slots={socialSlots}
              onSlotsChange={setSocialSlots}
              exportWidth={exportWidth}
              exportHeight={exportHeight}
              onCostAdd={handleCostAdd}
              onCreditsChanged={refreshCredits}
              hasHumanModel={hasHumanModel}
              // Transparent product PNG from the Confirm cutout step.
              // When set, StepSocialFinal skips OpenAI compose entirely
              // and renders the transparent image directly over the
              // design's slot color. AI is used only for the text
              // content (suggestSocialCopy stays unchanged).
              transparentProductImage={productTransparentImage}
              brandLogoUrl={brandLogoUrl}
              onBack={() => setStep(step - 1)}
              compact
              onSaveAsRecreate={handleSaveAsRecreate}
            />
          </Section>
        );
      }

      case 'quality':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Image quality"
            icon="ri-vip-crown-line"
            description="Pick a tier — affects credit cost per frame."
            state={state}
            summary={qualitySummary}
            onEdit={editAt(stepNum)}
            cta={continueBtn(handleQualityContinue, false)}
          >
            <StepQuality
              selected={quality}
              onSelect={handleQualitySelect}
              isCarousel={isCarousel}
              planTier={planTier}
              balance={balance}
              provider={provider}
              onProviderChange={setProvider}
              aspectRatio={platformAspectRatio}
              compact
            />
          </Section>
        );

      case 'preview': {
        // AI mode (product-shot / carousel) — render AiResultViewer +
        // auto-fire useEffect (defined earlier) handles the API call on
        // entry. Terminal step: no Continue, just Download + Back.
        if (hasDesignFork && !isSocialRoute && designMode === 'ai') {
          return (
            <Section
              key={id}
              step={stepNum}
              title={aiGenerating ? 'Designing your ad…' : aiError ? 'AI Generate failed' : 'Your AI-designed ad'}
              icon="ri-sparkling-2-line"
              description={
                aiGenerating
                  ? `AI is composing your ad. ${isCarousel ? '~60-80s for the 4 frames' : '~15-20s'}.`
                  : aiError
                    ? 'Something went wrong with the render. Try again below.'
                    : "Download below. Want a different take? Go back, optionally switch to a template, and re-Generate."
              }
              state={state}
            >
              <AiResultViewer
                result={aiResult}
                loading={aiGenerating}
                expectedFrameCount={isCarousel ? 4 : 1}
                error={aiError}
                onRetry={() => {
                  // Clear the prior artifact + error — the auto-fire
                  // useEffect picks it up immediately (deps include
                  // aiResult + aiError) and re-fires the API. Going
                  // through the effect avoids a double-fire race
                  // (clearing state synchronously, then awaiting
                  // setAiGenerating, would let the effect AND the
                  // manual call both run).
                  setAiError(null);
                  setAiResult(null);
                }}
                onBack={() => setStep(step - 1)}
              />
            </Section>
          );
        }
        return (
          <Section
            key={id}
            step={stepNum}
            title="Preview"
            icon="ri-image-line"
            description="Generation runs the moment this section opens. Use the controls below to download or continue to text."
            state={state}
            summary={previewSummary}
            onEdit={editAt(stepNum)}
          >
            <StepPreview
              productFile={productFiles[0] || null}
              // Multi-product carousel — pass the full ordered array
              // so StepPreview can route each frame to its mapped
              // product. Non-carousel routes get a length-1 array
              // (or undefined) which StepPreview handles identically
              // to the single-file path.
              productFiles={isCarouselRoute ? productFiles : undefined}
              // Multi-product carousel — pass per-product metadata so
              // each frame's gen call uses the matching name/desc.
              // Non-carousel routes leave these undefined; StepPreview
              // falls back to the singular productName/Description.
              productNames={isCarouselRoute ? productNames : undefined}
              productDescriptions={isCarouselRoute ? productDescriptions : undefined}
              platformId={platform || 'instagram'}
              platformAspectRatio={platformAspectRatio}
              templateId={activeTemplateId}
              productName={productName}
              productDescription={productDescription}
              quality={quality}
              onQualityChange={setQuality}
              provider={provider}
              // Fashion route — pass the detected garment region so backend
              // uses the matching mask file. canProceed gates Details →
              // Model on a real classification so by the time we render
              // Preview, garmentType is always a real region; the fallback
              // to 'top' is belt-and-suspenders only.
              garmentType={isFashionRoute
                ? (garmentType && garmentType !== 'none' ? garmentType : 'top')
                : undefined}
              isCarousel={isCarousel}
              planTier={planTier}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              lastGenKey={lastGenKey}
              onGenerationComplete={handleGenerationComplete}
              onCostAdd={handleCostAdd}
              onCreditsChanged={refreshCredits}
              onBack={() => setStep(step - 1)}
              onContinue={handlePreviewContinue}
            />
          </Section>
        );
      }

      case 'text':
        return (
          <Section
            key={id}
            step={stepNum}
            title="Add Text & Logo & Logo"
            icon="ri-text"
            description="Layer text presets and your brand logo onto the post. Save to My Creatives when you're happy."
            state={state}
          >
            <StepText
              platformAspectRatio={platformAspectRatio}
              templateId={activeTemplateId}
              isCarousel={isCarousel}
              overlays={textOverlays}
              onOverlaysChange={setTextOverlays}
              shapeOverlays={shapeOverlays}
              onShapeOverlaysChange={setShapeOverlays}
              logoOverlays={logoOverlays}
              onLogoOverlaysChange={setLogoOverlays}
              brandLogoUrl={brandLogoUrl}
              selectedPresetId={selectedPresetId}
              onPresetSelect={setSelectedPresetId}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              onRegenerateCopy={handleRegenerateCopy}
              onBack={() => setStep(step - 1)}
              compact
              onSaveAsRecreate={handleSaveAsRecreate}
            />
          </Section>
        );

      // 'video-generation' case was here — removed May 26 with the rest
      // of the video code. See VIDEO-RESEARCH.md.
    }
  };

  // Sidebar stepper was removed May 26 — the accordion's own locked/active/
  // completed state signaling makes a separate step list redundant. The
  // STEP_META map above is no longer consumed but kept around as a single
  // source of truth for section titles + icons in case we ever need them
  // for a future overview / progress UI.

  // Render-time guard: if the URL didn't carry a valid ?type=, the
  // useEffect above is in flight redirecting to /create. Render nothing
  // in the interim to avoid a flash of empty wizard chrome.
  if (postType === null) {
    return null;
  }

  return (
    <>
    <AppLayoutV2
      onNewPost={handleNewPost}
      onGallery={handleGallery}
    >
      {/* Single consistent column width across every section. Wide enough
          for Section 7's 3-col workspace (preset gallery | preview | layer
          editor ≈ 960px content), narrow enough to read tidy on ultrawide. */}
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {adConcept && (
          <div className="mb-5 rounded-xl border border-primary/25 bg-primary/5 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-primary mb-1 flex items-center gap-1">
                  <i className="ri-radar-line" /> From your Ad Strategist concept
                </p>
                {adConcept.headline && <p className="font-display font-bold text-dark text-base">{adConcept.headline}</p>}
                {adConcept.subhead && <p className="text-sm text-dark-muted">{adConcept.subhead}</p>}
                {adConcept.visual_direction && (
                  <p className="text-xs text-dark mt-1"><span className="font-semibold">Visual:</span> {adConcept.visual_direction}</p>
                )}
                {adConcept.template_hint && (
                  <p className="text-xs text-dark-muted mt-0.5 italic">Template idea: {adConcept.template_hint}</p>
                )}
              </div>
              <button
                onClick={() => setAdConcept(null)}
                className="text-dark-muted hover:text-dark cursor-pointer flex-shrink-0"
                title="Dismiss"
              >
                <i className="ri-close-line" />
              </button>
            </div>
          </div>
        )}
        <div className="mb-6">
          <h1 className="font-display font-bold text-dark text-2xl">Create a Post</h1>
          <p className="text-dark-muted text-sm mt-1">
            Complete each step in order. Scroll up to review or edit anything.
          </p>
        </div>

        {sections.map((id, idx) => renderSection(id, idx))}
      </div>
    </AppLayoutV2>

    {/* New Creative confirmation — fires from the sidebar CTA when the user
        has unsaved progress. Same copy + treatment as /app's modal. */}
    {showNewPostConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-amber-500 text-xl" />
          </div>
          <h3 className="font-display font-bold text-dark text-lg mb-2">
            Start a New Creative?
          </h3>
          <p className="text-dark-muted text-sm mb-2">
            You&apos;re about to start over from Step 1.
          </p>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
            <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
              <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0" />
              Your current progress will be lost. Save to My Creatives first if you want to keep it.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={cancelNewPost}
              className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
            >
              Keep Working
            </button>
            <button
              onClick={confirmNewPost}
              className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Gallery confirmation — fires from the sidebar Gallery row when
        the user has unsaved progress. */}
    {showExitConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl">
          <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
            <i className="ri-error-warning-line text-amber-500 text-xl" />
          </div>
          <h3 className="font-display font-bold text-dark text-lg mb-2">
            Exit Post Creator?
          </h3>
          <p className="text-dark-muted text-sm mb-2">
            You are about to leave the Post Creator and return to My Creatives.
          </p>
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
            <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
              <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0" />
              Your current progress will not be saved. You will need to start over if you return.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={cancelGallery}
              className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
            >
              Stay &amp; Continue
            </button>
            <button
              onClick={confirmGallery}
              className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
            >
              Exit to My Creatives
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ProductShotAiExplainer — AI tab content for Product Shot / Carousel
// background steps. Pure explainer card (no Generate button) — the
// actual wizardAiGenerate call fires later, automatically, when the
// user lands on the Preview step. Quality picked in between determines
// credit cost.
//
// Mirrors AiGeneratePanel's brand-context callout + "What you'll get"
// list so the two AI tab surfaces look like siblings.
function ProductShotAiExplainer({
  route,
  creativeDirection,
  onCreativeDirectionChange,
}: {
  route: 'single' | 'carousel' | 'product-shot' | 'social-post';
  creativeDirection: CreativeDirection;
  onCreativeDirectionChange: (cd: CreativeDirection) => void;
}) {
  const isCarousel = route === 'carousel';
  // Text density override is Product Shot route only. Carousel uses
  // _render_ai_magic_carousel_frame which has its own beat directives
  // (frame 4 specifically requires a CTA button) and isn't wired for
  // text_density yet — scoped as a separate decision per user direction.
  const showTextDensity = route === 'product-shot';
  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-accent/5 border border-accent/15 p-4 flex items-start gap-2">
        <i className="ri-information-line text-accent text-base mt-0.5" />
        <p className="text-xs text-dark-muted leading-relaxed">
          <span className="font-bold text-dark">AI uses your brand context</span> — name,
          brief, colors, and logo from <a href="/brand" className="text-primary font-bold underline">My Brand</a> — to
          stay on-tone. Update those to change the look without touching this step.
        </p>
      </div>

      {/* Filters live above the "Next" hint so the user can dial in
          their creative direction BEFORE advancing. The filters apply
          to the auto-fire on the Preview step. */}
      <CreativeDirectionFilters
        creativeDirection={creativeDirection}
        onCreativeDirectionChange={onCreativeDirectionChange}
        showTextDensity={showTextDensity}
      />

      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 flex items-start gap-2">
        <i className="ri-arrow-right-line text-primary text-base mt-0.5" />
        <p className="text-xs text-dark-muted leading-relaxed">
          <span className="font-bold text-dark">Next:</span> click <em className="not-italic font-bold">Continue</em> to
          pick a quality tier. Your ad renders automatically on the Preview step
          ({isCarousel ? '~60-80s for 4 frames' : '~15-20s'}).
        </p>
      </div>
    </div>
  );
}
